
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Package, Loader2, AlertCircle } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AppSearchInput } from "./AppSearchInput";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DeviceAppsAllAppsContentProps {
  deviceId: string;
}

export const DeviceAppsAllAppsContent = ({ deviceId }: DeviceAppsAllAppsContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [allApps, setAllApps] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastProcessedCursor, setLastProcessedCursor] = useState<string | null | undefined>(undefined);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Get device details using the correct API endpoint
  const deviceData = useQuery(api.devices.get.details, 
    deviceId ? { device: deviceId as any } : "skip"
  );
  
  const deviceSerial = deviceData?.device?.serial;
  
  // Use apps.list.allowedByDeviceFilter to get apps filtered by device permissions
  const allowedAppsData = useQuery(
    api.apps.list.allowedByDeviceFilter, 
    deviceSerial ? {
      serial: deviceSerial,
      paginationOpts: {
        cursor: cursor,
        numItems: 1000
      },
      query: debouncedQuery || undefined
    } : "skip"
  );

  // Reset pagination when debounced search query changes
  useEffect(() => {
    setCursor(null);
    setAllApps([]);
    setLastProcessedCursor(undefined);
  }, [debouncedQuery]);

  // Accumulate results when data arrives
  useEffect(() => {
    if (allowedAppsData?.page !== undefined) {
      // Check if we already processed this cursor to avoid duplicates
      if (cursor === lastProcessedCursor) {
        return;
      }
      
      setLastProcessedCursor(cursor);
      setIsLoadingMore(false);
      
      if (cursor === null) {
        // First page - reset
        setAllApps(allowedAppsData.page);
      } else {
        // Subsequent pages - append (avoid duplicates)
        setAllApps(prev => {
          const existingIds = new Set(prev.map((app: any) => app._id));
          const newApps = allowedAppsData.page.filter((app: any) => !existingIds.has(app._id));
          return [...prev, ...newApps];
        });
      }
    }
  }, [allowedAppsData, cursor, lastProcessedCursor]);

  // Load more function
  const loadMore = useCallback(() => {
    if (allowedAppsData && !allowedAppsData.isDone && allowedAppsData.continueCursor) {
      setIsLoadingMore(true);
      setCursor(allowedAppsData.continueCursor);
    }
  }, [allowedAppsData]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && allowedAppsData && !allowedAppsData.isDone) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, isLoadingMore, allowedAppsData]);

  // Only show initial loading when we have no apps yet
  const isInitialLoading = deviceData === undefined || 
    (deviceSerial && allowedAppsData === undefined && allApps.length === 0);

  // Sort apps alphabetically (API handles filtering via query param)
  const filteredApps = useMemo(() => {
    return [...allApps].sort((a: any, b: any) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }, [allApps]);


  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-600">Loading available apps...</span>
      </div>
    );
  }

  // Handle case where device has no deviceConfig
  if (!deviceData?.device?.deviceConfig) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Configuration</h3>
          <p className="text-gray-600 text-center mb-4 max-w-md">
            This device has no configuration. Please configure the device to see app settings.
          </p>
        </CardContent>
      </Card>
    );
  }


  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Search input */}
      <div className="w-full max-w-md">
        <AppSearchInput
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onCancel={() => setSearchQuery("")}
          placeholder="Search available apps..."
        />
      </div>

      {allApps.length === 0 && !isInitialLoading && !isLoadingMore ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Apps Available</h3>
            <p className="text-gray-600 text-center mb-4 max-w-md">
              No apps are currently available for this device based on whitelist, blacklist, level, and category settings.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex-1 border rounded-lg overflow-hidden">
          <ScrollArea className="h-[calc(100vh-400px)]">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10 border-b">
                <TableRow className="h-10">
                  <TableHead className="w-8 sm:w-12 px-1 sm:px-3 py-2">Icon</TableHead>
                  <TableHead className="min-w-0 px-1 sm:px-3 py-2">App Name</TableHead>
                  <TableHead className="hidden sm:table-cell w-20 sm:w-24 px-1 sm:px-3 py-2">Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApps.map((app: any) => (
                  <TableRow key={app._id} className="h-12">
                    <TableCell className="px-1 sm:px-3 py-2">
                      {app.iconUrl ? (
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-50 rounded border border-gray-200 flex items-center justify-center overflow-hidden">
                          <img
                            src={app.iconUrl}
                            alt={app.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                          <Package className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-1 sm:px-3 py-2 min-w-0">
                      <div className="truncate">
                        <div className="font-medium text-sm truncate">{app.name}</div>
                        <div className="text-xs text-gray-500 sm:hidden truncate">
                          {app.category}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-1 sm:px-3 py-2 hidden sm:table-cell">
                      <span className="text-xs text-gray-600 truncate">{app.category}</span>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredApps.length === 0 && searchQuery && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-gray-500 text-sm">
                      No available apps found matching "{searchQuery}"
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {/* Infinite scroll trigger */}
            <div ref={loadMoreRef} className="py-4 flex justify-center">
              {isLoadingMore && (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading more apps...</span>
                </div>
              )}
              {allowedAppsData && !allowedAppsData.isDone && !isLoadingMore && (
                <Button variant="ghost" size="sm" onClick={loadMore}>
                  Load more apps
                </Button>
              )}
              {allowedAppsData?.isDone && allApps.length > 0 && (
                <span className="text-xs text-gray-400">
                  Showing all {allApps.length} available apps
                </span>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
